<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { eqsApi } from '@/api/eqs';
import DelegateDialog from '@/components/DelegateDialog.vue';
import DelegationsSent from '@/components/DelegationsSent.vue';
import PageState from '@/components/PageState.vue';
import QuickUpdatePanel from '@/components/QuickUpdatePanel.vue';
import WorkSidePanel from '@/components/WorkSidePanel.vue';
import ProjectCard from '@/components/ProjectCard.vue';
import StatusChip from '@/components/StatusChip.vue';
import { notify } from '@/services/notice';
import type { DeliverableStatus, MyDeliverables, PortfolioResponse, WorkItem, WorkResponse } from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';
import { assertPortfolioResponse, duplicateProjectNumbers, healthFor, summaryFor , milestonesFor} from '@/utils/portfolio';
import { confirmAction } from '@/services/confirm';

const data = ref<WorkResponse | null>(null);
const portfolio = ref<PortfolioResponse | null>(null);
/**
 * 我负责的交付物（0904 Tony：推到首页待办）。
 * 不并进下面的队列：交付物的责任人几乎都是它所挂节点的责任人，
 * 队列里已经有那条节点待办，再进一条就是同一件事出现两次。
 * 这里按项目给几个数，点数字直接落到那个项目的交付物清单、带着筛选。
 * 接口挂了也不挡首页：卡片不出现，队列照常。
 */
const myDeliverables = ref<MyDeliverables | null>(null);
const loading = ref(true);
const error = ref('');
const portfolioError = ref('');
/*
  0904 Tony：「这个进去不就是和我的工作台一模一样的内容吗」——侧栏点「待我评审 2」
  落到这一页，但页面照旧是全量清单，那 2 条得自己在里面找。

  两个缺口一起补：
  ① **评审本来就筛不了**。清单里明明有 kind==='review' 的行（还带「去评审」按钮），
     但筛选只有 task / approval 两档。选任一档，评审行就消失且没有别的路把它调出来。
  ② **筛选状态不进 URL**，外面没法深链进来。

  现在 `?kind=review` 直接开在筛好的视图上，侧栏那个数字和点进来看到的条数对得上。
*/
type WorkKind = 'all' | 'task' | 'approval' | 'review';

const route = useRoute();
const router = useRouter();
const kind = ref<WorkKind>('all');
const timing = ref<'all' | 'urgent' | 'due-soon' | 'overdue'>('urgent');
const status = ref('all');
const moduleKey = ref('all');
const query = ref('');
const PAGE_SIZE = 20;
const visibleLimit = ref(PAGE_SIZE);
const actionQueue = ref<HTMLElement | null>(null);
const terminalStatuses = new Set(['completed', 'cancelled']);
// 派活(2026-08-25):布置任务不该非得开会;派出去的在「我派出的」里看得见下场
const delegating = ref(false);
const showSent = ref(false);

async function load() {
  loading.value = true;
  error.value = '';
  portfolioError.value = '';
  const [workResult, portfolioResult, deliverableResult] = await Promise.allSettled([
    eqsApi.work(),
    eqsApi.portfolio(),
    eqsApi.myDeliverables(),
  ]);
  myDeliverables.value = deliverableResult.status === 'fulfilled' ? deliverableResult.value : null;

  if (workResult.status === 'fulfilled') {
    if (!workResult.value || !Array.isArray(workResult.value.items)) {
      data.value = null;
      error.value = '我的工作响应缺少 items 数组';
    } else {
      data.value = workResult.value;
    }
  } else {
    data.value = null;
    error.value = errorMessage(workResult.reason);
  }

  if (portfolioResult.status === 'fulfilled') {
    try {
      assertPortfolioResponse(portfolioResult.value);
      portfolio.value = portfolioResult.value;
    } catch (caught) {
      portfolio.value = null;
      portfolioError.value = errorMessage(caught);
    }
  } else {
    portfolio.value = null;
    portfolioError.value = errorMessage(portfolioResult.reason);
  }

  loading.value = false;
}

async function updateStatus(item: WorkItem, next: string, label: string) {
  if (!canUpdateStatus(item)) return;
  if (!await confirmAction({ title: `确认对“${item.title}”执行“${label}”？` })) return;
  try {
    // 待办是三张源表的聚合视图，状态必须写回各自的源记录，
    // 否则计划任务和交付物的流转校验、进度事件和审计都会被绕过。
    if (item.source === 'plan-node') {
      if (item.projectId === null || item.projectId === undefined || item.referenceId === null) {
        throw new Error('该任务缺少项目或节点信息，无法更新状态');
      }
      await eqsApi.updatePlanNode(item.projectId, item.referenceId, { status: next });
    } else if (item.source === 'deliverable') {
      if (item.projectId === null || item.projectId === undefined || item.referenceId === null) {
        throw new Error('该交付物缺少项目信息，无法更新状态');
      }
      await eqsApi.advanceProjectDeliverableStatus(
        item.projectId,
        item.referenceId,
        next as DeliverableStatus,
      );
    } else {
      await eqsApi.updateWorkStatus(item.id, next);
    }
    notify('success', '工作状态已更新', `${item.title}：${label}`);
    await load();
  } catch (caught) {
    notify('error', '工作状态更新失败', errorMessage(caught));
  }
}

/** URL 里的 kind 只认这四个，别的（含手敲的乱值）一律当没传 */
function kindFromRoute(value: unknown): WorkKind | null {
  return value === 'task' || value === 'approval' || value === 'review' || value === 'all'
    ? value
    : null;
}

/*
  地址栏跟着筛选走，但**只写 kind 这一档**。
  timing/status/搜索词也塞进 URL 的话，人点几下就得到一条又长又脆的链接，
  分享出去还容易带上别人看不懂的状态；侧栏要的只是「开在哪一档上」。
*/
function syncKindToUrl(next: WorkKind): void {
  const query = { ...route.query };
  if (next === 'all') delete query.kind;
  else query.kind = next;
  void router.replace({ query });
}

async function applyOverviewFilter(nextKind: typeof kind.value, nextTiming: typeof timing.value) {
  kind.value = nextKind;
  syncKindToUrl(nextKind);
  timing.value = nextTiming;
  status.value = 'all';
  moduleKey.value = 'all';
  query.value = '';
  visibleLimit.value = PAGE_SIZE;
  await nextTick();
  actionQueue.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

function clearFilters() {
  kind.value = 'all';
  syncKindToUrl('all');
  timing.value = 'all';
  status.value = 'all';
  moduleKey.value = 'all';
  query.value = '';
  visibleLimit.value = PAGE_SIZE;
}

function canUpdateStatus(item: WorkItem): boolean {
  return item.canUpdateStatus && item.actions.length > 0;
}

function projectPath(item: WorkItem, section: 'overview' | 'plan'): string {
  return item.projectId === null || item.projectId === undefined
    ? item.recordPath
    : `/projects/${item.projectId}/${section}`;
}

/**
 * 「查看」要落到这条待办本身，不是项目首页。
 *
 * 之前所有待办的查看都指向 /projects/x/overview，点开还得自己再翻一遍找回刚才那条。
 *
 * 计划节点落**阶段工作的节点抽屉**，不是计划甘特（0915 Tony:「销售的任务点查看、
 * 开始、完成都找不到对应的交付项」）。甘特上只有一根条 —— 这条活要交什么、
 * 文件传哪儿、谁在评审，一样都看不到；那几样全在阶段工作的节点抽屉里。
 * 抽屉现在不看写权限也能打开，非项目经理进去是只读版 + 评审面板。
 */
function itemPath(item: WorkItem): string {
  if (item.projectId === null || item.projectId === undefined) return item.recordPath;
  if (item.source === 'plan-node' && item.referenceId !== null && item.referenceId !== undefined) {
    const node = encodeURIComponent(String(item.referenceId));
    return `/projects/${item.projectId}/activities?nodeId=${node}&edit=1`;
  }
  return projectPath(item, 'overview');
}

/**
 * 计划任务的「去完成」（0930 Tony：「工作台不能直接点击完成，不然很多具体的任务都没处理，偷懒直接完成了」）。
 * 工作台上不再一键完成：点这里进这条任务的抽屉，完成度直接拉到 100，
 * 抽屉里摆着还差什么——缺哪几份交付物文件，或者要写一句完成说明。保存还是人自己点。
 * 「已退回」不能一步转成完成（后端流转表），不给。
 */
function completePath(item: WorkItem): string | null {
  if (item.source !== 'plan-node' || item.referenceId === null || item.referenceId === undefined) return null;
  if (item.projectId === null || item.projectId === undefined) return null;
  if (!['pending', 'in_progress', 'blocked', ''].includes(item.status ?? '')) return null;
  return `${itemPath(item)}&focus=complete`;
}

function statusLabel(value: string): string {
  return {
    pending: '待处理',
    in_progress: '处理中',
  }[value] || value;
}

function moduleLabel(value: string): string {
  return {
    'project-management': '项目管理',
    'project-plan': '项目计划',
    project: '项目管理',
    quality: '质量管理',
  }[value] || value;
}

/*
  还没到开始时间的计划任务不算眼下的待办（0930 Tony：「P04 阶段任务按正常是还没触发的」）。
  判据在后端 plan-node-trigger.ts：计划开始已到或 7 天内要开始、没排开始日期、或已经动手才算触发。
  没触发的不藏：清单底下折叠成「还没到开始时间」一组，只给「查看」——
  当天就有人对着一个 10-28 才开始的任务点了「完成」。
*/
const openItems = computed<WorkItem[]>(() =>
  (data.value?.items || []).filter((item) => !terminalStatuses.has(item.status)));
const actionableItems = computed<WorkItem[]>(() => openItems.value.filter((item) => !item.notStarted));
const upcomingItems = computed<WorkItem[]>(() => openItems.value
  .filter((item) => item.notStarted)
  .sort((left, right) => (left.startDate || '9999').localeCompare(right.startDate || '9999')
    || left.title.localeCompare(right.title, 'zh-CN')));
/** 后端分页前数的；一次最多取 100 条，列出来的可能比它少 */
const upcomingTotal = computed(() => Math.max(data.value?.upcoming ?? 0, upcomingItems.value.length));
const upcomingVisible = computed(() => upcomingItems.value.length > 0 && (kind.value === 'all' || kind.value === 'task'));
const items = computed<WorkItem[]>(() => actionableItems.value.filter((item) =>
  (kind.value === 'all' || item.kind === kind.value)
  && (timing.value === 'all'
    || (timing.value === 'urgent' && ['overdue', 'due-soon'].includes(item.timing))
    || item.timing === timing.value)
  && (status.value === 'all' || item.status === status.value)
  && (moduleKey.value === 'all' || item.moduleKey === moduleKey.value)
  && (!query.value.trim() || [
    item.title,
    item.projectNo,
    item.moduleKey,
    item.assignee,
  ].filter(Boolean).join(' ').toLocaleLowerCase().includes(query.value.trim().toLocaleLowerCase())))
  .sort((left, right) => {
    const rank = { overdue: 0, 'due-soon': 1, normal: 2 } as Record<string, number>;
    return (rank[left.timing] ?? 3) - (rank[right.timing] ?? 3)
      || (left.dueDate || '9999').localeCompare(right.dueDate || '9999')
      || left.title.localeCompare(right.title, 'zh-CN');
  }));
/** 零待办时六张统计卡全是 0：收进折叠，不占首屏；有待办照旧直接摆出来 */
const summaryFolded = computed(() => actionableItems.value.length === 0);
const urgentItems = computed<WorkItem[]>(() => actionableItems.value
  .filter((item) => item.timing === 'overdue' || item.timing === 'due-soon')
  .sort((left, right) =>
    Number(right.timing === 'overdue') - Number(left.timing === 'overdue')
    || (left.dueDate || '9999').localeCompare(right.dueDate || '9999')));
const statuses = computed(() => [...new Set(actionableItems.value.map((item) => item.status))]);
const moduleKeys = computed(() => [...new Set(actionableItems.value.map((item) => item.moduleKey))]);
const visibleItems = computed(() => items.value.slice(0, visibleLimit.value));
const remainingItems = computed(() => Math.max(0, items.value.length - visibleItems.value.length));
/**
 * 按项目分组：工程师的实际做法是「先把某个项目这批清掉」，
 * 而不是在几十条跨项目待办里按到期日跳着做。
 * 分组顺序沿用已排好的紧急度顺序（组内首条最紧急的排前面）。
 */
const groupedVisibleItems = computed(() => {
  const groups = new Map<string, {
    key: string;
    projectId: number | string | null;
    projectNo: string;
    projectName: string;
    items: WorkItem[];
  }>();
  for (const item of visibleItems.value) {
    const key = item.projectId === null || item.projectId === undefined
      ? 'none'
      : String(item.projectId);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        projectId: item.projectId ?? null,
        projectNo: item.projectNo || '非项目事项',
        projectName: item.projectName || '',
        items: [],
      };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  return [...groups.values()];
});
/** 后端在分页前算出的真实总数；接口未返回时退回本地条数，避免显示 0。还没到开始时间的不算在内 */
const totalOpenItems = computed(() => (data.value?.total ?? actionableItems.value.length)
  - (data.value?.upcoming ?? 0));
const overdueGroupCount = (group: { items: WorkItem[] }) =>
  group.items.filter((item) => item.timing === 'overdue').length;
const overdueCount = computed(() => actionableItems.value.filter((item) => item.timing === 'overdue').length);
const dueSoonCount = computed(() => actionableItems.value.filter((item) => item.timing === 'due-soon').length);
const taskCount = computed(() => actionableItems.value.filter((item) => item.kind === 'task').length);
const approvalCount = computed(() => actionableItems.value.filter((item) => item.kind === 'approval').length);
const reviewCount = computed(() => actionableItems.value.filter((item) => item.kind === 'review').length);
const activeQueueLabel = computed(() => {
  if (timing.value === 'urgent') return '今日关注';
  if (timing.value === 'overdue') return '已逾期';
  if (timing.value === 'due-soon') return '即将到期';
  if (kind.value === 'task') return '我的任务';
  if (kind.value === 'approval') return '待审批';
  if (kind.value === 'review') return '待我评审';
  return '全部待办';
});
const myProjects = computed(() => (portfolio.value?.projects || []).filter((project) => project.isCurrentUserMember === true));
/** 卡片只在有事可做时出现：全 0 的卡片是噪音 */
const deliverableCardVisible = computed(() => {
  const value = myDeliverables.value;
  return Boolean(value && (value.total > 0 || value.toDecide > 0));
});
/** 每个数字落到那个项目的交付物清单，带上对应筛选 */
function deliverableLink(projectId: number, quick: string): string {
  return `/projects/${projectId}/deliverables?quick=${quick}`;
}
const projectConflicts = computed(() => duplicateProjectNumbers(portfolio.value?.projects || []));

/*
  从 URL 开局：侧栏「待我评审 2」点进来就该**已经筛好**，
  而不是把人扔进全量清单里自己找——那就真成了和主菜单同一个入口。
  时间档一并放开：按类型看的时候不该再被「今日关注」二次收窄。
*/
function adoptRouteKind(): void {
  const next = kindFromRoute(route.query.kind);
  if (!next) return;
  kind.value = next;
  if (next !== 'all') timing.value = 'all';
}
adoptRouteKind();
// 已经停在这一页时再点侧栏另一档，query 变了也要跟着换
watch(() => route.query.kind, adoptRouteKind);

onMounted(load);
</script>

<template>
  <PageState v-if="loading" state="loading" title="正在读取我的项目与待办" message="项目按成员关系识别，待办只按当前责任人读取。" />
  <template v-else>
    <PageState v-if="error" state="error" title="个人待办接口不可用" message="未回退到演示任务，也未将项目成员误作待办责任人。" :detail="error" @retry="load" />
    <template v-else-if="data">
      <section class="work-toolbar" aria-label="派活">
        <button class="button button-primary" type="button" @click="delegating = true">派活</button>
        <button class="button button-secondary" type="button" :aria-expanded="showSent" @click="showSent = !showSent">
          {{ showSent ? '收起我派出的' : '我派出的' }}
        </button>
        <p>把要办的事直接派给具体的人:对方工作台立刻出现,你在「我派出的」里追踪下场。</p>
      </section>
      <section v-if="showSent" class="sent-panel" aria-label="我派出的任务">
        <DelegationsSent />
      </section>
      <div class="work-columns">
      <section class="today-dashboard">
        <header class="dashboard-heading">
          <div>
            <span>待办</span>
            <h2>今日行动</h2>
          </div>
          <p v-if="!summaryFolded">点击数字，下面的清单会立即切换到对应事项。</p>
        </header>
        <!-- 零待办时整排统计卡收进 details（默认收起）；有待办时外层只是个不带样式的 div -->
        <component :is="summaryFolded ? 'details' : 'div'" :class="{ 'work-summary-fold': summaryFolded }">
        <summary v-if="summaryFolded">查看待办统计筛选（当前均为 0）</summary>
        <div class="work-summary" aria-label="待办快捷筛选">
          <button type="button" class="focus" :class="{ 'is-active': timing === 'urgent' }" :aria-pressed="timing === 'urgent'" @click="applyOverviewFilter('all', 'urgent')">
            <span>今日关注</span><strong>{{ urgentItems.length }}</strong><small>逾期及 7 日内到期</small>
          </button>
          <button type="button" class="risk" :class="{ 'is-active': timing === 'overdue' }" :aria-pressed="timing === 'overdue'" @click="applyOverviewFilter('all', 'overdue')">
            <span>已逾期</span><strong>{{ overdueCount }}</strong><small>需要优先处理</small>
          </button>
          <button type="button" class="warning" :class="{ 'is-active': timing === 'due-soon' }" :aria-pressed="timing === 'due-soon'" @click="applyOverviewFilter('all', 'due-soon')">
            <span>即将到期</span><strong>{{ dueSoonCount }}</strong><small>未来 7 天</small>
          </button>
          <button type="button" :class="{ 'is-active': kind === 'task' && timing === 'all' }" :aria-pressed="kind === 'task' && timing === 'all'" @click="applyOverviewFilter('task', 'all')">
            <span>我的任务</span><strong>{{ taskCount }}</strong><small>已到开始时间的未完成任务</small>
          </button>
          <button type="button" :class="{ 'is-active': kind === 'approval' && timing === 'all' }" :aria-pressed="kind === 'approval' && timing === 'all'" @click="applyOverviewFilter('approval', 'all')">
            <span>待审批</span><strong>{{ approvalCount }}</strong><small>需要我审批</small>
          </button>
          <button type="button" :class="{ 'is-active': kind === 'review' && timing === 'all' }" :aria-pressed="kind === 'review' && timing === 'all'" @click="applyOverviewFilter('review', 'all')">
            <span>待我评审</span><strong>{{ reviewCount }}</strong><small>轮到我出结论</small>
          </button>
        </div>
        </component>

        <section v-if="deliverableCardVisible && myDeliverables" class="my-deliverables" aria-label="我负责的交付物">
          <header>
            <div>
              <span>交付物</span>
              <h3>我负责的交付物</h3>
            </div>
            <p>{{ myDeliverables.total }} 条没做完<template v-if="myDeliverables.toDecide">，另有 {{ myDeliverables.toDecide }} 条轮到你审批</template>。点数字进清单，传文件、提交审批都在行上。</p>
          </header>
          <div class="dlv-kpis">
            <b v-if="myDeliverables.toDecide" class="is-turn"><strong>{{ myDeliverables.toDecide }}</strong>轮到我审</b>
            <b class="is-risk"><strong>{{ myDeliverables.overdue }}</strong>已逾期</b>
            <b><strong>{{ myDeliverables.noFile }}</strong>缺文件</b>
            <b><strong>{{ myDeliverables.ready }}</strong>可提交</b>
            <b v-if="myDeliverables.rejected" class="is-risk"><strong>{{ myDeliverables.rejected }}</strong>被退回</b>
            <b v-if="myDeliverables.inReview"><strong>{{ myDeliverables.inReview }}</strong>审批中</b>
          </div>
          <table class="dlv-projects">
            <thead>
              <tr><th>项目</th><th>缺文件</th><th>可提交</th><th>被退回</th><th>已逾期</th><th>轮到我审</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="project in myDeliverables.projects" :key="project.projectId">
                <td><b>{{ project.projectNo }}</b><small v-if="project.projectName">{{ project.projectName }}</small></td>
                <td><RouterLink v-if="project.noFile" :to="deliverableLink(project.projectId, 'no-file')">{{ project.noFile }}</RouterLink><span v-else>—</span></td>
                <td><RouterLink v-if="project.ready" :to="deliverableLink(project.projectId, 'ready')">{{ project.ready }}</RouterLink><span v-else>—</span></td>
                <td><RouterLink v-if="project.rejected" class="is-risk" :to="deliverableLink(project.projectId, 'rejected')">{{ project.rejected }}</RouterLink><span v-else>—</span></td>
                <td><RouterLink v-if="project.overdue" class="is-risk" :to="deliverableLink(project.projectId, 'overdue')">{{ project.overdue }}</RouterLink><span v-else>—</span></td>
                <td><RouterLink v-if="project.toDecide" class="is-turn" :to="deliverableLink(project.projectId, 'decide')">{{ project.toDecide }}</RouterLink><span v-else>—</span></td>
                <td><RouterLink class="queue-action" :to="deliverableLink(project.projectId, 'mine')">我的全部</RouterLink></td>
              </tr>
            </tbody>
          </table>
        </section>

        <section id="all-work-items" ref="actionQueue" class="action-queue" aria-live="polite">
          <header class="queue-heading">
            <div>
              <span>当前清单</span>
              <h2>{{ activeQueueLabel }}</h2>
              <strong>{{ items.length }} 条</strong>
            </div>
            <button v-if="kind !== 'all' || timing !== 'all' || status !== 'all' || moduleKey !== 'all' || query" type="button" class="reset-button" @click="clearFilters">查看全部待办</button>
          </header>

          <div class="queue-controls">
            <label class="queue-search">
              <span>快速查找</span>
              <input v-model="query" type="search" placeholder="输入任务、项目编号" @input="visibleLimit = PAGE_SIZE">
            </label>
            <label>
              <span>处理状态</span>
              <select v-model="status" @change="visibleLimit = PAGE_SIZE">
                <option value="all">全部状态</option>
                <option v-for="item in statuses" :key="item" :value="item">{{ statusLabel(item) }}</option>
              </select>
            </label>
            <label>
              <span>业务模块</span>
              <select v-model="moduleKey" @change="visibleLimit = PAGE_SIZE">
                <option value="all">全部模块</option>
                <option v-for="item in moduleKeys" :key="item" :value="item">{{ moduleLabel(item) }}</option>
              </select>
            </label>
          </div>

          <QuickUpdatePanel @applied="load" />

          <div v-if="visibleItems.length" class="work-list queue-work-list">
            <template v-for="group in groupedVisibleItems" :key="group.key">
              <header class="work-group">
                <b>{{ group.projectNo }}</b>
                <span v-if="group.projectName">{{ group.projectName }}</span>
                <em>{{ group.items.length }} 条<template v-if="overdueGroupCount(group)">，{{ overdueGroupCount(group) }} 条逾期</template></em>
                <RouterLink
                  v-if="group.projectId !== null"
                  class="queue-action"
                  :to="`/projects/${group.projectId}/plan`"
                >项目计划</RouterLink>
              </header>
            <article v-for="item in group.items" :key="item.id">
              <StatusChip :value="item.timing" :label="item.kind === 'approval' ? '审批' : item.kind === 'review' ? '评审' : item.timing === 'overdue' ? '已逾期' : item.timing === 'due-soon' ? '即将到期' : '任务'" />
              <div class="work-copy">
                <b>{{ item.title }}</b>
                <span>{{ moduleLabel(item.moduleKey) }} · {{ statusLabel(item.status) }}<template v-if="item.assignee"> · {{ item.assignee }}</template></span>
              </div>
              <time :class="{ risk: item.timing === 'overdue', warning: item.timing === 'due-soon' }">{{ formatDate(item.dueDate) }}</time>
              <div class="work-actions">
                <template v-if="canUpdateStatus(item)">
                  <RouterLink class="queue-action primary" :to="itemPath(item)">查看</RouterLink>
                  <RouterLink v-if="completePath(item)" class="queue-action" :to="completePath(item)!">去完成</RouterLink>
                  <button
                    v-for="action in item.actions"
                    :key="action.status"
                    class="inline-button"
                    type="button"
                    @click="updateStatus(item, action.status, action.label)"
                  >{{ action.label }}</button>
                </template>
                <!--
                  审批待办：只给「去审批」（0916）。
                  原来它掉进通用分支，配了开始 / 完成两个按钮——点「完成」只把这条待办
                  关掉，不写意见也不推进单子，单子就卡在没人手上（线上卡了 30 张）。
                  出结论只有交付物页那一个口。
                -->
                <template v-else-if="item.kind === 'approval' && item.projectId !== null && item.projectId !== undefined">
                  <RouterLink
                    class="queue-action primary"
                    :to="`/projects/${item.projectId}/deliverables?quick=decide`"
                  >去审批</RouterLink>
                </template>
                <template v-else-if="item.kind === 'review' && item.projectId !== null && item.projectId !== undefined && item.referenceId !== null">
                  <!-- 评审待办直接落到那个节点的抽屉，评审面板就在里面 -->
                  <RouterLink
                    class="queue-action primary"
                    :to="`/projects/${item.projectId}/activities?nodeId=${encodeURIComponent(String(item.referenceId))}&edit=1&focus=review`"
                  >去评审</RouterLink>
                </template>
                <template v-else>
                  <RouterLink class="queue-action primary" :to="projectPath(item, 'overview')">项目</RouterLink>
                  <RouterLink v-if="item.projectId !== null && item.projectId !== undefined" class="queue-action" :to="projectPath(item, 'plan')">计划</RouterLink>
                </template>
              </div>
            </article>
            </template>
          </div>
          <div v-else class="compact-empty list-empty">
            <b>{{ actionableItems.length
              ? '当前筛选没有结果'
              : upcomingTotal ? '眼下没有已到开始时间的待办' : '当前账号没有未完成待办' }}</b>
            <span>{{ actionableItems.length
              ? '换一个条件，或返回全部未完成待办。'
              : upcomingTotal
                ? `这里只列责任人是当前账号、已到开始时间的未完成项；另有 ${upcomingTotal} 条还没到开始时间，见下方。`
                : '这里只列责任人是当前账号、已到开始时间的未完成项。可以查看我的项目、用上方「派活」布置任务，或去任务跟踪看任务进展。' }}</span>
            <button v-if="actionableItems.length" class="queue-action primary" type="button" @click="clearFilters">查看全部待办</button>
            <div v-else class="compact-empty-actions">
              <RouterLink class="queue-action primary" to="/portfolio">查看我的项目</RouterLink>
              <RouterLink class="queue-action" to="/tasks">去任务跟踪</RouterLink>
            </div>
          </div>

          <footer v-if="remainingItems" class="queue-footer">
            <span>已显示 {{ visibleItems.length }} / {{ items.length }} 条（当前账号未完成共 {{ totalOpenItems }} 条）</span>
            <button type="button" @click="visibleLimit += PAGE_SIZE">再显示 {{ Math.min(PAGE_SIZE, remainingItems) }} 条</button>
          </footer>
          <footer v-else-if="visibleItems.length > 6" class="queue-footer">
            <span>已显示全部 {{ items.length }} 条</span>
            <button type="button" @click="visibleLimit = 6">收起到前 6 条</button>
          </footer>

          <!--
            还没到开始时间的计划任务：计划开始前 7 天才进待办、才会被催。
            折叠着放在最后，只给「查看」不给开始 / 完成——真要提前做，进阶段工作的节点抽屉里报。
          -->
          <details v-if="upcomingVisible" class="upcoming-work">
            <summary>
              还没到开始时间 <b>{{ upcomingTotal }}</b> 条
              <small>计划开始前 7 天才进上面的清单、才会催；要提前做，点「查看」到阶段工作里报进度</small>
            </summary>
            <ul>
              <li v-for="item in upcomingItems" :key="item.id">
                <div>
                  <b>{{ item.title }}</b>
                  <span>{{ item.projectNo }}<template v-if="item.startDate"> · {{ formatDate(item.startDate) }} 开始</template><template v-if="item.dueDate"> · {{ formatDate(item.dueDate) }} 到期</template></span>
                </div>
                <RouterLink class="queue-action" :to="itemPath(item)">查看</RouterLink>
              </li>
            </ul>
            <p v-if="upcomingTotal > upcomingItems.length" class="upcoming-more">
              另有 {{ upcomingTotal - upcomingItems.length }} 条没列出来（一次最多取 100 条待办），到项目的阶段工作里看全部。
            </p>
          </details>
        </section>
      </section>

      <WorkSidePanel :work-items="actionableItems" />
      </div>
    </template>

    <details class="content-panel my-projects">
      <summary class="projects-summary">
        <div>
          <span>项目</span>
          <h2>我的项目</h2>
          <p>需要查看项目时再展开，避免干扰今天的处理清单。</p>
        </div>
        <strong>{{ myProjects.length }} 个</strong>
        <b class="projects-toggle"><span class="expand-label">展开</span><span class="collapse-label">收起</span></b>
      </summary>
      <div class="projects-body">
        <PageState
          v-if="portfolioError"
          state="error"
          title="我的项目暂时无法读取"
          message="个人待办仍可独立使用。"
          :detail="portfolioError"
          @retry="load"
        />
        <div v-else-if="portfolio && myProjects.length" class="project-grid">
          <ProjectCard
            v-for="project in myProjects"
            :key="project.id"
            :project="project"
            :summary="summaryFor(portfolio, project.id)"
            :health="healthFor(portfolio, project.id)"
          :milestones="milestonesFor(portfolio, project.id)"
            :conflict="projectConflicts.has(project.projectNo)"
          />
        </div>
        <PageState
          v-else
          state="empty"
          title="当前账号尚未关联项目"
          message="如有遗漏，请联系项目管理员维护项目成员关系；这里不会用个人待办反推项目归属。"
        />
      </div>
    </details>
  </template>
  <DelegateDialog v-if="delegating" @close="delegating = false" @created="showSent = true" />
</template>

<style scoped>
/* 还没到开始时间（0930）：折叠在清单最后，只给「查看」。summary 设 flex 会吃掉原生三角，自己画一个 */
.upcoming-work { margin-top: 12px; border: 1px dashed var(--line); border-radius: var(--radius-s); background: var(--white); }
.upcoming-work > summary {
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; list-style: none; cursor: pointer; color: var(--ink); font-size: 13px; font-weight: 700;
}
.upcoming-work > summary::-webkit-details-marker { display: none; }
.upcoming-work > summary::before { content: '▸'; color: var(--muted); font-size: 12px; }
.upcoming-work[open] > summary::before { content: '▾'; }
.upcoming-work > summary b { color: var(--blue); }
.upcoming-work > summary small { color: var(--muted); font-size: 12px; font-weight: 400; }
.upcoming-work ul { margin: 0; padding: 0 14px 8px; list-style: none; }
.upcoming-work li {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 8px 0; border-top: 1px solid var(--line);
}
.upcoming-work li > div { display: grid; gap: 2px; min-width: 0; }
.upcoming-work li b { font-size: 13px; }
.upcoming-work li span { color: var(--muted); font-size: 12px; }
.upcoming-more { margin: 0; padding: 0 14px 10px; color: var(--muted); font-size: 12px; }
.work-toolbar {
  display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
  padding: 12px 16px; border: 1px solid var(--line); border-radius: 12px; background: var(--white);
}
.work-toolbar p { margin: 0; color: var(--muted); font-size: 12.5px; }
.sent-panel {
  margin-bottom: 14px; padding: 16px 18px;
  border: 1px solid var(--line); border-radius: 12px; background: var(--white);
}

/* 双栏：左边做事，右边拍板和看动态。窄屏叠回单栏，右栏在前——
   手机上先看「别人在等我什么」，再刷长清单 */
.work-columns { display: grid; grid-template-columns: minmax(0, 1fr) 330px; gap: 16px; align-items: start; }
@media (max-width: 1100px) { .work-columns { grid-template-columns: 1fr; } .work-side { order: -1; } }
.today-dashboard {
  margin-bottom: 12px;
  padding: 18px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, .92);
}

.dashboard-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 20px;
  padding: 0 2px 12px;
}

.dashboard-heading > div {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.dashboard-heading span,
.queue-heading span,
.projects-summary > div > span {
  color: var(--blue);
  /* 11px 下限：这些眉标里混着中文（「当前清单」「我的项目」），
     中文字形在 9px 基本糊成一团，拉丁小型大写字母还能勉强读 */
  font: 800 11px var(--display);
  letter-spacing: .12em;
}

.dashboard-heading h2 {
  margin: 0;
  font: 700 23px/1 var(--display);
}

.dashboard-heading p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}

.today-dashboard .work-summary {
  margin: 0;
  border-color: var(--line);
}

/* 零待办时的统计卡折叠：只露一行，原生三角保留 */
.work-summary-fold { border: 1px dashed var(--line); border-radius: var(--radius-s); background: var(--white); }
.work-summary-fold > summary { padding: 10px 14px; cursor: pointer; color: var(--muted); font-size: 13px; font-weight: 700; }

.work-summary button {
  position: relative;
  min-height: 76px;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  align-content: center;
  gap: 2px 10px;
  padding: 11px 16px;
  transition: background 120ms ease, color 120ms ease, box-shadow 120ms ease;
}

.work-summary button:hover {
  z-index: 1;
  background: rgba(18, 109, 166, .06);
  box-shadow: inset 0 -3px var(--blue);
}

.work-summary button span {
  align-self: end;
  font-size: 12px;
  font-weight: 700;
}

.work-summary button strong {
  grid-row: 1 / 3;
  grid-column: 2;
  align-self: center;
  margin: 0;
  font-size: 31px;
}

.work-summary button small {
  grid-column: 1;
  color: var(--muted);
  font-size: 12px;
}

.work-summary button.is-active {
  box-shadow: inset 0 -4px var(--blue);
}

.work-summary button.is-active:hover {
  background: var(--blue);
}

.work-summary button.is-active small {
  color: rgba(255, 255, 255, .72);
}

/* 我负责的交付物：一张小卡，数字都能点 */
.my-deliverables { margin-top: 12px; padding: 14px 16px; border: 1px solid rgba(0,117,174,.3); border-radius: var(--radius-s); background: rgba(0,117,174,.04); }
.my-deliverables > header { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 6px 16px; }
.my-deliverables > header span { color: var(--blue); font: 800 11px var(--display); letter-spacing: .12em; }
.my-deliverables > header h3 { margin: 2px 0 0; font: 700 17px/1.2 var(--display); }
.my-deliverables > header p { margin: 0; color: var(--muted); font-size: 12px; }
.dlv-kpis { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.dlv-kpis b { display: inline-flex; align-items: baseline; gap: 6px; padding: 4px 10px; border: 1px solid var(--line); border-radius: 999px; background: var(--white); font-size: 12px; font-weight: 700; }
.dlv-kpis b strong { font-size: 16px; }
.dlv-kpis b.is-risk { color: var(--risk); border-color: rgba(196,63,56,.35); }
.dlv-kpis b.is-turn { color: var(--blue); border-color: var(--blue); }
.dlv-projects { width: 100%; margin-top: 10px; border-collapse: collapse; font-size: 12px; }
.dlv-projects th { padding: 4px 8px; border-bottom: 1px solid var(--line); color: var(--muted); font-weight: 700; text-align: right; white-space: nowrap; }
.dlv-projects th:first-child, .dlv-projects td:first-child { text-align: left; }
.dlv-projects td { padding: 6px 8px; border-bottom: 1px solid var(--line); text-align: right; white-space: nowrap; }
.dlv-projects td:first-child b { color: var(--blue); }
.dlv-projects td:first-child small { display: block; color: var(--muted); font-weight: 400; white-space: normal; }
.dlv-projects td a { color: var(--blue); font-weight: 800; text-decoration: underline; }
.dlv-projects td a.is-risk { color: var(--risk); }
.dlv-projects td a.queue-action { text-decoration: none; }
.dlv-projects td span { color: var(--muted); }

.action-queue {
  scroll-margin-top: 88px;
  margin-top: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  background: var(--white);
}

.queue-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 58px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--line);
  color: var(--ink);
  background: var(--paper-2);
}

.queue-heading > div {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.queue-heading span {
  color: var(--blue);
}

.queue-heading h2 {
  margin: 0;
  font: 700 23px/1 var(--display);
}

.queue-heading strong {
  color: var(--muted);
  font-size: 12px;
}

.reset-button {
  min-height: 34px;
  padding: 0 11px;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
  color: var(--ink);
  background: var(--white);
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.reset-button:hover {
  border-color: var(--blue);
  color: var(--blue);
}

.queue-controls {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) 160px 180px;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}

.queue-controls label {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 9px;
}

.queue-controls label > span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.queue-controls input,
.queue-controls select {
  width: 100%;
  min-height: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--line);
  color: var(--ink);
  background: var(--white);
  font-size: 13px;
}

.queue-work-list {
  padding: 0 14px;
}

.queue-work-list article {
  min-height: 59px;
  /* 动作列放得下三个按钮（查看 / 去完成 / 开始，0930 起计划任务多了「去完成」）；116px 时「去完成」会折成两行 */
  grid-template-columns: 78px minmax(220px, 1fr) 96px 156px;
  gap: 12px;
}

.queue-work-list .work-copy {
  min-width: 0;
  gap: 2px;
}

.queue-work-list .work-copy b {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-work-list .work-copy span,
.queue-work-list time {
  font-size: 12px;
}

.work-actions {
  display: flex !important;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 5px !important;
}

.queue-action {
  min-width: 44px;
  min-height: 32px;
  display: inline-grid;
  place-items: center;
  padding: 0 8px;
  white-space: nowrap;
  border: 1px solid var(--line);
  color: var(--blue);
  background: var(--white);
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.queue-action.primary {
  border-color: var(--blue);
  color: white;
  background: var(--blue);
}

.compact-empty {
  display: grid;
  justify-items: start;
  gap: 7px;
  min-height: 150px;
  align-content: center;
  padding: 22px;
}

.compact-empty > span {
  color: var(--muted);
  font-size: 12px;
}

.compact-empty-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.queue-footer {
  min-height: 47px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 14px;
  border-top: 1px solid var(--line);
  color: var(--muted);
  background: var(--paper);
  font-size: 12px;
}

.queue-footer button {
  min-height: 32px;
  padding: 0 11px;
  border: 1px solid var(--blue);
  color: var(--blue);
  background: var(--white);
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.my-projects {
  margin-bottom: 12px;
  padding: 0;
}

.projects-summary {
  min-height: 76px;
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 18px;
  padding: 13px 18px;
  cursor: pointer;
  list-style: none;
}

.projects-summary::-webkit-details-marker {
  display: none;
}

.projects-summary > div {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: baseline;
  gap: 4px 12px;
}

.projects-summary h2 {
  margin: 0;
  font: 700 22px/1 var(--display);
}

.projects-summary p {
  grid-column: 1 / -1;
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}

.projects-summary > strong {
  font: 700 22px var(--display);
}

.projects-toggle {
  min-width: 58px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  color: var(--blue);
  font-size: 12px;
  text-align: center;
}

.collapse-label {
  display: none;
}

.my-projects[open] .expand-label {
  display: none;
}

.my-projects[open] .collapse-label {
  display: inline;
}

.projects-body {
  padding: 0 18px 18px;
  border-top: 1px solid var(--line);
}

.projects-body .project-grid {
  margin-top: 18px;
}

@media (max-width: 600px) {
  .dashboard-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .work-summary button {
    min-height: 72px;
  }

  .queue-controls {
    grid-template-columns: 1fr;
  }

  .queue-controls label {
    grid-template-columns: 82px 1fr;
  }

  .queue-work-list article {
    grid-template-columns: 74px 1fr;
  }

  .queue-work-list time,
  .queue-work-list .work-actions {
    grid-column: 2;
  }

  .queue-work-list .work-actions {
    justify-content: flex-start;
  }

  .projects-summary {
    grid-template-columns: 1fr auto;
  }

  .projects-summary > strong {
    display: none;
  }

  .projects-summary p {
    display: none;
  }
}
.work-group { display: flex; align-items: baseline; gap: 8px; padding: 10px 12px 6px; border-top: 1px solid var(--line); background: var(--paper); }
.work-group:first-child { border-top: 0; }
.work-group b { color: var(--blue); font: 800 12px var(--display); }
.work-group span { overflow: hidden; color: var(--ink); font-size: 12px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.work-group em { flex: 1; color: var(--muted); font-size: 12px; font-style: normal; }
</style>
